import { accountIdFormat } from "@redotech/redo-account-id";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import {
  consolidationGroupIdFormat,
  consolidationIdFormat,
  returnsCenterIdFormat,
} from "@redotech/redo-warehouse-id";
import * as z from "zod";

export const ManualConsolidationSubjectSchema = z.discriminatedUnion("type", [
  z.object({
    consolidationGroupId: consolidationGroupIdFormat.schema,
    type: z.literal("group"),
  }),
  z.object({
    merchantAccountId: accountIdFormat.schema,
    returnsCenterId: returnsCenterIdFormat.schema,
    type: z.literal("merchant"),
  }),
]);

const ManualConsolidationOptionBaseSchema = z.object({
  configuredDestination: z.boolean(),
  configuredOriginLocationId: locationIdFormat.schema.nullable(),
  destinationName: z.string(),
  existingConsolidationId: consolidationIdFormat.schema.nullable(),
  name: z.string(),
  originName: z.string(),
  participantNames: z.array(z.string()),
  state: z.enum(["available", "existing"]),
});

export const ManualConsolidationOptionSchema = z.discriminatedUnion("type", [
  ManualConsolidationOptionBaseSchema.extend({
    consolidationGroupId: consolidationGroupIdFormat.schema,
    type: z.literal("group"),
  }),
  ManualConsolidationOptionBaseSchema.extend({
    merchantAccountId: accountIdFormat.schema,
    returnsCenterId: returnsCenterIdFormat.schema,
    type: z.literal("merchant"),
  }),
]);

export type ManualConsolidationSubject = z.infer<
  typeof ManualConsolidationSubjectSchema
>;
export type ManualConsolidationOption = z.infer<
  typeof ManualConsolidationOptionSchema
>;

export const ManualConsolidationLocationSchema = z.object({
  id: locationIdFormat.schema,
  isReturnsCenter: z.boolean(),
  name: z.string(),
});

export type ManualConsolidationLocation = z.infer<
  typeof ManualConsolidationLocationSchema
>;

const ManualConsolidationDestinationSelectionSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({ kind: z.literal("configured") }),
    z.object({
      kind: z.literal("location"),
      locationId: locationIdFormat.schema,
    }),
    z.object({
      address: z.object({
        address1: z.string().trim().min(1),
        address2: z.string().trim().nullable(),
        city: z.string().trim().min(1),
        countryCode: z.string().trim().length(2),
        name: z.string().trim().min(1),
        province: z.string().trim().nullable(),
        zip: z.string().trim().min(1),
      }),
      kind: z.literal("address"),
    }),
  ],
);

export type ManualConsolidationDestinationSelection = z.infer<
  typeof ManualConsolidationDestinationSelectionSchema
>;

export const getManualConsolidationOptions = {
  name: "getManualConsolidationOptions" as const,
  input: z.object({}),
  output: z.object({
    locations: z.array(ManualConsolidationLocationSchema),
    options: z.array(ManualConsolidationOptionSchema),
  }),
};

export const createManualConsolidation = {
  name: "createManualConsolidation" as const,
  input: z.object({
    closeDueAt: z.date().nullable().default(null),
    route: z.object({
      destination: ManualConsolidationDestinationSelectionSchema,
      originLocationId: locationIdFormat.schema,
    }),
    subject: ManualConsolidationSubjectSchema,
  }),
  output: z.object({
    consolidationId: consolidationIdFormat.schema,
    status: z.enum(["created", "existing"]),
  }),
};
